/**
 * Coerce an API-provided result total into a finite, non-negative integer.
 *
 * The search API reports `hits.total.value` as a number, but totals can
 * arrive missing, null, NaN, or (defensively) as a formatted string. Anything
 * that cannot be parsed as a positive count is treated as 0 so pagination
 * stops instead of looping indefinitely against a bad value.
 *
 * @param {*} value - Raw total from the search response.
 * @returns {number} Non-negative integer count.
 */
export const parseTotal = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
  }

  return 0;
};

/**
 * Compute the highest page number that can be requested for a given total
 * and page size. Returns 0 when there is nothing to paginate.
 *
 * @param {number} total - Total result count (already sanitized via parseTotal).
 * @param {number} limit - Results per page.
 * @returns {number} Max page index (>= 1 when total > 0, else 0).
 */
export const maxPage = (total, limit) => {
  if (!(total > 0)) {
    return 0;
  }

  const size = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 1;
  return Math.ceil(total / size);
};
